import {createReducer, ActionType} from 'typesafe-actions'
import {v4 as uuid} from 'uuid'

import {
  OptionalItem,
  createDefaultOption,
  createOptionWithValue,
} from 'airship/patterns/Option'

import * as actions from './actions'

export const privateHeaderKeys = [
  // lower case only
  'authorization',
]

export interface Header {
  id: string
  key: string
  value: string
}

export interface DefaultValue {
  key: string
  default: string
}

export interface ResponsePath {
  id: string
  name: string
  path: string
}

export type ActivePage = 1 | 2 | 3

export interface XDFFormMeta {
  addOrEdit: 'add' | 'edit'
  modalIsOpen: boolean
  activePage: ActivePage
  valid: boolean
  showValidationErrors: boolean
  databaseID: string
  createdOn: string
  updatedOn: string
  requestURLChanged: boolean
}

export interface XDFForm {
  meta: XDFFormMeta
  friendlyName: string
  feedID: string
  description: string
  requestURL: string
  confirmedDomain: string
  cacheResponse: boolean
  confirmedAccessRights: boolean
  defaultValues: ReadonlyArray<DefaultValue>
  headers: ReadonlyArray<Header>
  responsePaths: ReadonlyArray<ResponsePath>
  throttleRate: OptionalItem<number>
}

export type Actions = ActionType<typeof actions>

export const defaultForm: XDFForm = {
  meta: {
    addOrEdit: 'add',
    modalIsOpen: false,
    activePage: 1,
    valid: false,
    showValidationErrors: false,
    databaseID: '',
    createdOn: '',
    updatedOn: '',
    requestURLChanged: false,
  },
  friendlyName: '',
  feedID: '',
  description: '',
  requestURL: '',
  confirmedDomain: '',
  cacheResponse: false,
  confirmedAccessRights: false,
  defaultValues: [],
  headers: [],
  responsePaths: [],
  throttleRate: createDefaultOption(),
}

export default createReducer<XDFForm, Actions>({
  ...defaultForm,
})
  // open / close actions
  .handleAction(actions.openXDFForm, () => {
    return {...defaultForm, meta: {...defaultForm.meta, modalIsOpen: true}}
  })
  .handleAction(actions.openXDFFormToEdit, (state, {payload}) => {
    const defaultValues: DefaultValue[] = []
    for (const key in payload.default_values) {
      const defaultValue = payload.default_values[key]
      if (defaultValue !== undefined) {
        defaultValues.push({
          key,
          default: defaultValue,
        })
      }
    }

    const headers: Header[] = []
    for (const key in payload.headers) {
      const headerValue = payload.headers[key]
      if (headerValue !== undefined) {
        headers.push({
          key,
          value: headerValue,
          id: uuid(),
        })
      }
    }

    const responsePaths: ResponsePath[] = payload.response_mappings.map(
      ({display_name: name, original_path: path}) => ({
        id: uuid(),
        name,
        path,
      })
    )

    const throttleRate: OptionalItem<number> = payload.throttle_rate
      ? createOptionWithValue(payload.throttle_rate)
      : createDefaultOption()

    return {
      ...defaultForm,
      friendlyName: payload.display_name,
      feedID: payload.name,
      description: payload.description,
      requestURL: payload.url,
      cacheResponse: payload.request_cache_enabled,
      defaultValues,
      headers,
      responsePaths,
      throttleRate,
      meta: {
        ...defaultForm.meta,
        databaseID: payload.id,
        createdOn: payload.created_on,
        updatedOn: payload.updated_on,
        valid: true,
        addOrEdit: 'edit',
        modalIsOpen: true,
      },
    }
  })
  .handleAction(actions.closeXDFForm, () => {
    return {...defaultForm}
  })
  // form meta actions
  .handleAction(actions.setActivePage, (state, {payload}) => {
    return {...state, meta: {...state.meta, activePage: payload}}
  })
  .handleAction(actions.setFormIsValid, (state, {payload}) => {
    return {...state, meta: {...state.meta, valid: payload}}
  })
  .handleAction(actions.setShowValidationErrors, (state, {payload}) => {
    return {...state, meta: {...state.meta, showValidationErrors: payload}}
  })
  // form field actions
  .handleAction(actions.setFriendlyName, (state, {payload}) => {
    return {...state, friendlyName: payload}
  })
  .handleAction(actions.setFeedID, (state, {payload}) => {
    return {...state, feedID: payload}
  })
  .handleAction(actions.setDescription, (state, {payload}) => {
    return {...state, description: payload}
  })
  .handleAction(actions.setRequestURL, (state, {payload}) => {
    return {
      ...state,
      requestURL: payload,
      meta: {...state.meta, requestURLChanged: true},
    }
  })
  .handleAction(actions.setConfirmedDomain, (state, {payload}) => {
    return {...state, confirmedDomain: payload}
  })
  .handleAction(actions.setCacheResponse, (state, {payload}) => {
    return {
      ...state,
      cacheResponse: payload,
      throttleRate: createDefaultOption(),
    }
  })
  .handleAction(actions.setConfirmedAccessRights, (state, {payload}) => {
    return {...state, confirmedAccessRights: payload}
  })
  .handleAction(actions.setValidPlaceholders, (state, {payload}) => {
    const newDefaultValues: DefaultValue[] = []

    payload.forEach((placeholder: string) => {
      const alreadyInList = newDefaultValues.filter(
        (dv) => dv.key === placeholder
      ).length
      if (alreadyInList) return // this will avoid duplicate entries

      const matchingCurrentDefault = state.defaultValues.filter(
        (dv) => dv.key === placeholder
      )
      if (matchingCurrentDefault[0] !== undefined) {
        newDefaultValues.push({
          key: placeholder,
          default: matchingCurrentDefault[0].default,
        })
      } else {
        newDefaultValues.push({
          key: placeholder,
          default: '',
        })
      }
    })
    return {...state, defaultValues: newDefaultValues}
  })
  .handleAction(actions.setDefaultValue, (state, {payload}) => {
    return {
      ...state,
      defaultValues: state.defaultValues.map((dv) => {
        if (dv.key === payload.key) return payload
        else return dv
      }),
    }
  })
  .handleAction(actions.addHeader, (state, {payload}) => {
    return {
      ...state,
      headers: state.headers.concat({
        ...payload,
        id: uuid(),
      }),
    }
  })
  .handleAction(actions.updateHeader, (state, {payload}) => {
    return {
      ...state,
      headers: state.headers.map((header) =>
        header.id === payload.id ? payload : header
      ),
    }
  })
  .handleAction(actions.removeHeader, (state, {payload}) => {
    return {
      ...state,
      headers: state.headers.filter((header) => header.id !== payload),
    }
  })
  .handleAction(actions.addResponsePath, (state, {payload}) => {
    return {
      ...state,
      responsePaths: state.responsePaths.concat({
        ...payload,
        id: uuid(),
      }),
    }
  })
  .handleAction(actions.updateResponsePath, (state, {payload}) => {
    return {
      ...state,
      responsePaths: state.responsePaths.map((responsePath) =>
        responsePath.id === payload.id ? payload : responsePath
      ),
    }
  })
  .handleAction(actions.removeResponsePath, (state, {payload}) => {
    return {
      ...state,
      responsePaths: state.responsePaths.filter(
        (responsePath) => responsePath.id !== payload
      ),
    }
  })
  .handleAction(actions.toggleThrottleRate, (state, {payload}) => {
    let option
    if (payload) {
      option = createOptionWithValue(Number.NaN)
    } else {
      option = createDefaultOption()
    }
    return {...state, throttleRate: option}
  })
  .handleAction(actions.setThrottleRate, (state, {payload}) => {
    return {
      ...state,
      throttleRate: createOptionWithValue(payload),
    }
  })
